import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { fetchZones, type Zone } from '@/lib/api/zones'
import { getMapProvider, type GeoJsonFeatureCollection } from '@/lib/map/provider'
import { StartParkingDialog } from './StartParkingDialog'

// Color zones by hourly price tier so users can see cheap vs premium areas at a glance.
function fillColorForZone(z: Zone): string {
  const price = z.tariff?.hourlyPriceUzs ?? 0
  if (price >= 12000) return '#dc2626' // red — premium
  if (price >= 10000) return '#f59e0b' // amber — mid-high
  if (price >= 8000) return '#0284c7' // blue — mid
  return '#16a34a' // green — cheap / unknown
}

// Convert API territory ([[[lat, lng], ...]]) to GeoJSON Polygon coordinates
// ([[[lng, lat], ...]]). GeoJSON requires ring closure (first === last) and ≥4 points.
function ringsToGeoJson(rings: number[][][]): number[][][] | null {
  const out: number[][][] = []
  for (const ring of rings) {
    if (!Array.isArray(ring) || ring.length < 3) continue
    const swapped = ring.map(([lat, lng]) => [lng, lat] as [number, number])
    const first = swapped[0]
    const last = swapped[swapped.length - 1]
    if (!first || !last) continue
    if (first[0] !== last[0] || first[1] !== last[1]) {
      swapped.push([first[0], first[1]])
    }
    if (swapped.length >= 4) out.push(swapped)
  }
  return out.length > 0 ? out : null
}

function zonesToFeatureCollection(zones: Zone[]): GeoJsonFeatureCollection {
  const features: GeoJsonFeatureCollection['features'] = []
  for (const z of zones) {
    if (!z.territoryCoordinates) continue
    const coords = ringsToGeoJson(z.territoryCoordinates)
    if (!coords) continue
    features.push({
      type: 'Feature',
      geometry: { type: 'Polygon', coordinates: coords },
      properties: {
        id: z.id,
        code: z.code,
        title: z.title ?? z.name,
        priceUzs: z.tariff?.hourlyPriceUzs ?? null,
        fillColor: fillColorForZone(z),
      },
    })
  }
  return { type: 'FeatureCollection', features }
}

export function MapView() {
  const { t } = useTranslation()
  const containerRef = useRef<HTMLDivElement>(null)
  const providerRef = useRef<ReturnType<typeof getMapProvider> | null>(null)
  const [selected, setSelected] = useState<Zone | null>(null)
  const zonesById = useRef<Map<string, Zone>>(new Map())

  const { data: zones, isLoading, error } = useQuery({
    queryKey: ['zones'],
    queryFn: fetchZones,
  })

  useEffect(() => {
    if (!containerRef.current) return
    const provider = getMapProvider()
    providerRef.current = provider
    let cancelled = false
    void provider
      .mount(containerRef.current, {
        center: { lat: 41.3111, lng: 69.2797 },
        zoom: 12,
      })
      .then(() => {
        if (cancelled) return
        provider.on('zone-click', (id) => {
          const z = zonesById.current.get(id)
          if (z) setSelected(z)
        })
      })
    return () => {
      cancelled = true
      provider.unmount()
    }
  }, [])

  useEffect(() => {
    if (!zones || !providerRef.current) return
    zonesById.current = new Map(zones.map((z) => [z.id, z]))
    providerRef.current.setZones({
      id: 'all',
      geoJson: zonesToFeatureCollection(zones),
    })
    providerRef.current.setMarkers(
      zones.map((z) => ({
        id: z.id,
        label: z.title ?? z.code,
        lat: z.centroidLat,
        lng: z.centroidLng,
      })),
    )
  }, [zones])

  return (
    <div className="relative">
      <div ref={containerRef} className="h-[600px] w-full bg-slate-100" />
      {isLoading && (
        <div className="pointer-events-none absolute left-3 top-3 rounded bg-white/90 px-3 py-1 text-xs text-slate-600 shadow">
          {t('map.loading')}
        </div>
      )}
      {error && (
        <div className="absolute left-3 top-3 rounded bg-rose-50 px-3 py-1 text-xs text-rose-700 shadow">
          {t('map.load_error')}
        </div>
      )}
      {selected && (
        <StartParkingDialog zone={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  )
}
